import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

/**
 * MT1-1428: one row per cloned item on the post-clone edit screen. `initial*`
 * hold the values the worker generated (auto number, copied name) so the
 * dialog can PATCH only the rows the user actually changed.
 */
export interface CloneEditRow {
  id: string;
  itemNumber: string;
  name: string;
  initialItemNumber: string;
  initialName: string;
  status: 'idle' | 'saving' | 'saved' | 'error';
  error?: string;
}

export const isCloneEditRowChanged = (row: CloneEditRow) =>
  row.itemNumber !== row.initialItemNumber || row.name !== row.initialName;

export interface CloneItemEditStepProps {
  rows: CloneEditRow[];
  saving: boolean;
  onRowChange: (id: string, patch: Partial<Pick<CloneEditRow, 'itemNumber' | 'name'>>) => void;
}

export const CloneItemEditStep = ({ rows, saving, onRowChange }: CloneItemEditStepProps) => {
  const { t } = useTranslation();

  return (
    <>
      <Typography variant="body2" sx={{ mb: 2 }}>
        {t('items.clone.edit.description', {
          defaultValue:
            'Update the item number and name for each cloned item, or skip to keep the generated values.',
        })}
      </Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width="40%">{t('items.clone.edit.columnNumber', 'Item Number')}</TableCell>
              <TableCell>{t('items.clone.edit.columnName', 'Item Name')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const numberEmpty = row.itemNumber.trim() === '';
              const nameEmpty = row.name.trim() === '';
              // Column headers aren't programmatically tied to the inputs, so
              // each field carries its own row-scoped accessible name.
              const rowLabel = (column: string) => `${column} ${index + 1}`;
              return (
                <TableRow key={row.id}>
                  <TableCell>
                    <TextField
                      value={row.itemNumber}
                      onChange={(event) => {
                        onRowChange(row.id, { itemNumber: event.target.value });
                      }}
                      error={numberEmpty || row.status === 'error'}
                      helperText={
                        numberEmpty
                          ? t('items.clone.edit.numberRequired', 'Item number is required')
                          : row.status === 'error'
                            ? row.error
                            : undefined
                      }
                      disabled={saving}
                      size="small"
                      fullWidth
                      slotProps={{
                        htmlInput: {
                          'data-testid': `clone-edit-number-${row.id}`,
                          'aria-label': rowLabel(t('items.clone.edit.columnNumber', 'Item Number')),
                        },
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      value={row.name}
                      onChange={(event) => {
                        onRowChange(row.id, { name: event.target.value });
                      }}
                      error={nameEmpty}
                      helperText={
                        nameEmpty
                          ? t('items.clone.edit.nameRequired', 'Item name is required')
                          : undefined
                      }
                      disabled={saving}
                      size="small"
                      fullWidth
                      slotProps={{
                        htmlInput: {
                          'data-testid': `clone-edit-name-${row.id}`,
                          'aria-label': rowLabel(t('items.clone.edit.columnName', 'Item Name')),
                        },
                      }}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};
